import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2, type LucideIcon } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { Icon } from './Icon'

/**
 * Button — the workhorse CTA primitive.
 *
 * Pencil sources: Button/{Primary,Secondary,Ghost,Destructive,Outline,Link,Icon}
 * IDs: 2C1sv EuaDB Wgkxz GaRli QwcLS qhg4i lTS9W L68r8 mAOCr 6eTOp
 *
 * Variants:
 *   primary · secondary · ghost · destructive · outline · link
 * Sizes:
 *   sm · md (default) · lg · icon
 * States:
 *   default · hover · focus-visible · active · disabled · loading
 */
export const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium',
    // Bastion look: solid/outline buttons render UPPERCASE with a touch of
    // tracking. ghost + link opt back out (see those variants) so icon
    // buttons and inline links stay normal-case.
    'uppercase tracking-wide',
    'transition-[background-color,color,border-color,box-shadow] duration-150',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        // Primary CTA = Cymmetri MINT fill (the guide's "contained primary =
        // mint" rule). Blue is reserved for links / nav / avatars / focus.
        // Hover / active step to the deeper mint shades.
        primary: [
          'bg-cta text-cta-foreground',
          'hover:bg-[var(--color-cta-hover)]',
          'active:bg-[var(--color-cta-active)]',
        ].join(' '),
        secondary: [
          'bg-secondary text-secondary-foreground border border-ia-border',
          'hover:bg-ia-elevated hover:border-ia-green/40',
          'active:bg-ia-elevated active:brightness-95',
        ].join(' '),
        ghost: [
          'bg-transparent text-ia-text normal-case tracking-normal',
          // Green-tinted accent halo on hover keeps every icon-button
          // visually consistent with the sidebar active treatment.
          'hover:bg-[var(--color-ia-sidebar-active-glow)] hover:text-[var(--color-ia-sidebar-active-fg)]',
          'active:brightness-95',
        ].join(' '),
        destructive: [
          'bg-destructive text-destructive-foreground',
          'hover:brightness-110 hover:shadow-[0_0_0_3px_var(--color-ia-error-bg)]',
          'active:brightness-95 active:shadow-none',
        ].join(' '),
        outline: [
          'bg-transparent text-ia-text border border-ia-border',
          'hover:bg-ia-surface-hover hover:border-ia-green/50',
          'active:bg-ia-elevated',
        ].join(' '),
        link: 'bg-transparent text-ia-text-link normal-case tracking-normal underline-offset-4 hover:underline hover:text-ia-green h-auto p-0',
      },
      size: {
        sm: 'h-8 px-3 text-body-sm [&_svg]:size-[14px]',
        md: 'h-10 px-4 text-body-md [&_svg]:size-[16px]',
        lg: 'h-12 px-6 text-body-md [&_svg]:size-[18px]',
        // Icon-only buttons are ALWAYS circles — hover + active + focus share
        // the same silhouette so the shape never morphs from square to round.
        // Pair with variant='ghost' for top-bar / toolbar icon buttons so
        // only the accent halo is visible on hover.
        icon: 'h-10 w-10 p-0 rounded-pill [&_svg]:size-[18px]',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
  leftIcon?: LucideIcon
  rightIcon?: LucideIcon
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild,
      loading,
      leftIcon,
      rightIcon,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button'
    // Radix Slot requires exactly ONE React child (it calls
    // React.Children.only internally). When asChild=true we pass
    // children through untouched — the caller's <Link> / <a> / whatever
    // is the single child Slot expects. Icon / loading decoration is
    // SKIPPED in asChild mode; callers wanting a leading icon inside
    // an asChild Button should render it inside their child element:
    //
    //   <Button asChild>
    //     <Link className="inline-flex items-center gap-2">
    //       <Icon /> Label
    //     </Link>
    //   </Button>
    //
    // Without this short-circuit, the JSX <Slot>{null}{children}{null}</Slot>
    // emits a 3-element array to Slot's React.Children.only, which
    // throws "expected to receive a single React element child" and
    // crashes the entire subtree — even when leftIcon/rightIcon are
    // undefined, because {null} still counts as a child slot in JSX
    // arrays.
    if (asChild) {
      return (
        <Comp
          ref={ref}
          className={cn(buttonVariants({ variant, size, className }))}
          disabled={disabled || loading}
          aria-busy={loading || undefined}
          {...props}
        >
          {children}
        </Comp>
      )
    }
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? (
          <Loader2 className="animate-spin" strokeWidth={1.75} aria-hidden />
        ) : leftIcon ? (
          <Icon icon={leftIcon} size={size === 'sm' ? 'xs' : size === 'lg' ? 'md' : 'sm'} />
        ) : null}
        {children}
        {!loading && rightIcon ? (
          <Icon icon={rightIcon} size={size === 'sm' ? 'xs' : size === 'lg' ? 'md' : 'sm'} />
        ) : null}
      </Comp>
    )
  },
)
Button.displayName = 'Button'
